import { authHeaders, throwOnError } from "./authHeaders";

/**
 * Client for /api/officers.
 *
 * Officer records carry PII (name, personal email, phone, Emory N number), so
 * every route behind this module requires a supervisor session and is scoped
 * server-side to the caller's hospital grants. Do not cache these responses
 * anywhere that outlives the session.
 */

export type OfficerStatus = "ACTIVE" | "PENDING" | "INACTIVE" | "SUSPENDED" | "REJECTED";

export interface Officer {
  id: string;
  n_number: string;
  name: string;
  email: string | null;
  phone: string | null;
  hospital_id: string;
  hospital_name: string | null;
  status: OfficerStatus;
  created_at: string;
  updated_at: string | null;
  /** Present when the officer is currently signed on to a shift. */
  active_session_id: string | null;
  session_started: string | null;
  session_scans: number | null;
  /** 'SELF' = first-scan self-enrollment from a handset (starts PENDING). */
  enrolled_via?: "SUPERVISOR" | "SELF" | null;
  enroll_lat?: number | null;
  enroll_lng?: number | null;
  /** Metres between the enrollment sign-on GPS and the site's recorded scan area. */
  enroll_distance_m?: number | null;
  enroll_at?: string | null;
  enroll_scanner_id?: string | null;
  /** Site the sign-on GPS pointed to at enrollment — may differ from the officer's confirmed site. */
  enroll_gps_hospital_id?: string | null;
  enroll_gps_hospital_name?: string | null;
}

export interface OfficerInput {
  n_number: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  hospital_id: string;
}

/** Single-officer detail (GET /api/officers/:id) — adds lifetime totals. */
export interface OfficerDetail extends Officer {
  total_scans: number;
  total_sessions: number;
  approved_at?: string | null;
}

/** Activity aggregates returned when ?include=activity is passed.
 *  All figures are bounded to the requested window (default: last 30 days). */
export interface OfficerActivity {
  range_days: number;
  range_start?: string;
  range_end?: string;
  /** Scans within the window (all scans, GPS or not). */
  window_scans?: number;
  /** Shift sessions overlapping the window. */
  window_sessions?: number;
  active_days: number;
  first_scan_at: string | null;
  last_scan_at: string | null;
  daily: { day: string; count: number }[];
}

/**
 * Fetch one officer plus their activity aggregates (KPIs + daily scan series)
 * for the officer detail page. Pass startDate/endDate (ISO) to bound the
 * aggregates to a specific window; defaults to the last 30 days. The map
 * points are loaded separately via fetchScanMap({ officerId }).
 */
export async function fetchOfficerActivity(
  id: string,
  window?: { startDate?: string; endDate?: string }
): Promise<{ officer: OfficerDetail; activity: OfficerActivity }> {
  const query = new URLSearchParams({ include: "activity" });
  if (window?.startDate) query.set("startDate", window.startDate);
  if (window?.endDate) query.set("endDate", window.endDate);
  const response = await fetch(
    `/api/officers/${encodeURIComponent(id)}?${query.toString()}`,
    { headers: authHeaders() }
  );
  await throwOnError(response, "Failed to load officer");
  const body = await response.json();
  return { officer: body.officer, activity: body.activity };
}

export async function fetchOfficers(params: {
  hospitalId?: string;
  /** "ACTIVE" (server default), "INACTIVE", "SUSPENDED", or "ALL". */
  status?: string;
} = {}): Promise<Officer[]> {
  const query = new URLSearchParams();
  if (params.hospitalId) query.set("hospital_id", params.hospitalId);
  if (params.status) query.set("status", params.status);

  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/officers${suffix}`, { headers: authHeaders() });
  await throwOnError(response, "Failed to load officers");
  const body = await response.json();
  return body.officers ?? [];
}

export async function createOfficer(input: OfficerInput): Promise<Officer> {
  const response = await fetch("/api/officers", {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(input),
  });
  await throwOnError(response, "Failed to create officer");
  const body = await response.json();
  return body.officer;
}

export async function updateOfficer(
  id: string,
  input: Partial<OfficerInput> & { status?: OfficerStatus }
): Promise<Officer> {
  const response = await fetch(`/api/officers/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: authHeaders(true),
    body: JSON.stringify(input),
  });
  await throwOnError(response, "Failed to update officer");
  const body = await response.json();
  return body.officer;
}

/**
 * Deactivates an officer. The API soft-deletes — the record is retained and set
 * to INACTIVE, and any open shift session is closed. Scan history stays
 * attributed, which is the point: a patrol record must not lose its officer
 * because the account was later disabled.
 */
export async function deactivateOfficer(id: string): Promise<void> {
  const response = await fetch(`/api/officers/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  await throwOnError(response, "Failed to deactivate officer");
}

/**
 * Approve a pending self-enrollment: confirms the identity (optionally with a
 * corrected name) and activates the officer. The API stamps approved_by/-at.
 */
export async function approveOfficer(id: string, name?: string): Promise<Officer> {
  return updateOfficer(id, { status: "ACTIVE", ...(name ? { name } : {}) });
}

/**
 * Reject a pending self-enrollment: the record is kept as REJECTED (so the
 * badge cannot silently re-enroll) and any open shift is closed. Scans made
 * while pending stay attributed to the rejected record, where they remain
 * visible for review rather than melting back into unattributed.
 */
export async function rejectOfficer(id: string): Promise<Officer> {
  return updateOfficer(id, { status: "REJECTED" });
}
